import {Injectable} from '@angular/core';
import {AngularFirestore} from '@angular/fire/firestore';
import {SessionService} from './session.service';
import {CommonService} from './common.service';
import {COLLECTION_NAME, COLOR_SHUBETSU, IKUSEI, IKUSUU, QUERY_LIMIT_SHIYOULOT} from '../../shared/constants';
import firebase from 'firebase';
import firestore = firebase.firestore;
import {MessageService} from './message.service';
import {MasterService} from './master.service';
import {Observable, Subscription} from 'rxjs';
import {ShiyoulotService} from './shiyoulot.service';
import {
  CellData,
  ColorRef,
  ColorRefList,
  Layout,
  LayoutSearchKey,
  WasuuPerColumn
} from '../../shared/interfaces/layout';
import {ShiyoulotV2} from '../../shared/interfaces/shiyoulot';
import {yyyyMMdd} from '../../shared/typeAliases';
import {Keisha} from '../../shared/interfaces/keisha';
import {SearchKeyForShiyoulot2} from '../../shared/interfaces/search-key-for-shiyoulot';

/**
 * mat-grid の grid 一つに持たせるデータ
 */
export interface GridTileInfo {
  bikou: string,
  cellWasuu: number | null,
  color: string,
  cols: number,
  customerName: string,
  edukeDate: yyyyMMdd,
  hidden: boolean,
  shiyoulotNumber: number | null,
  keishuName: string,
  rows: number,
  standardWasuu: number,
  taisoku: boolean,
  text: string,
  totalWasuu: number,
  yPosition: number,
  hover?: boolean
}

/**
 * mat-grid の grid を編集した内容を
 * Layout に反映させるためのデータ
 */
interface GridTileInfoForUpdateLayout {
  floor: number,
  column: number,
  side: number,
  gridTileIndex: number,
  gridTile: GridTileInfo
}

/**
 * 1列内の同じ内容の塊のグリッドデータ
 * 空のデータも一つのグリッドデータに別れる
 */
type ChunkedGridTileInfo = Record<string, GridTileInfo>;

/**
 * 1サイド分のグリッドデータ
 * 2サイド2グリッドデータの場合: { 0: { 0: GridTileInfo, 1: GridTileInfo }, 1: { 0: GridTileInfo, 1: GridTileInfo } }
 * 1列両側にゲージがある想定で基本2サイド
 */
type SideGridTileInfo = Record<string, ChunkedGridTileInfo>;

/**
 * 1列分のグリッドデータ
 * 1列目2サイド1グリッドデータの場合: { 0: { 0: { 0: GridTileInfo }, 1: { 0: GridTileInfo } } }
 */
type ColumnGridTileInfo = Record<string, SideGridTileInfo>;

/**
 * 1階分のグリッドデータ
 * 1階1列目2サイド1グリッドデータの場合: { 0: { 0: { 0: { 0: GridTileInfo }, 1: { 0: GridTileInfo } } } }
 *
 */
type FloorGridTileInfo = Record<string, ColumnGridTileInfo>;

/**
 * 階毎の飼養ロット羽数
 */
interface ShiyoulotWasuuPerFloor {
  shiyoulotNumber: number | null,
  color: string,
  wasuuArray: {floor: number, totalWasuu: number}[]
}

const PDF = 'pdf';
const PRINT = 'print';

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  selectedLayout: Layout = this.getInitLayout();
  selectedKeisha: Keisha | null = null;
  bikou = '';
  layoutObservable = new Observable<Layout[]>();
  shiyoulotObservable = new Observable<ShiyoulotV2[]>();
  shiyoulotObservableForDisplay = new Observable<ShiyoulotV2[]>();
  shiyoulotList: ShiyoulotV2[] = [];
  shiyoulotListForDisplay: ShiyoulotV2[] = []; // 既にLayout にある飼養ロット番号配列から取得した飼養ロット
  sameShiyoulotWasuuPerFloorList: ShiyoulotWasuuPerFloor[] = [];
  gridList: FloorGridTileInfo = {};
  selectedGridInfo: GridTileInfoForUpdateLayout | null = null;
  mode: typeof PDF | typeof PRINT= 'pdf';
  // TODO 一つの Subscription にまとめる。
  shiyoulotSubscription: Subscription = new Subscription();
  shiyoulotForDisplaySubscription: Subscription = new Subscription();

  constructor(private db: AngularFirestore,
              private sessionService: SessionService,
              private commonService: CommonService,
              public masterService: MasterService,
              public shiyoulotService: ShiyoulotService,
              private messageService: MessageService) { }

  /**
   * レイアウト初期値返す
   */
  getInitLayout() {
    const today = this.commonService.getDateWithString();
    const data = {
      keishaId: '',
      farmId: '',
      koushinKaisuu: 0,
      shiyoulotNumberArray: [],
      floorTitle: [],
      bikou: [],
      colorRefList: [],
      layout: {},
      yuukouDate: this.commonService.genOffsetDateWithStandardDateString(today, 7),
      shikkouDate: this.commonService.genOffsetDateWithStandardDateString(today, 56), // 育雛鶏舎の失効日初期値をセット
      yuukouDateArray: [],
    };
    return data;
  }

  /**
   * CustomerRootCollectionRef 取得
   * @param collectionName
   */
  genCustomerRootCollectionRef(collectionName: string) : firebase.firestore.CollectionReference<firebase.firestore.DocumentData> {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.firestore.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId).collection(collectionName);
  }

  /**
   * レイアウトobservable 取得
   * @param kensakuKey
   */
  getLayoutObservable(kensakuKey: LayoutSearchKey) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.LAYOUT, ref => {
        let query: firestore.CollectionReference |
            firestore.Query = ref;
        if (kensakuKey.farmId) {
          query = query.where('farmId', '==', kensakuKey.farmId);
        }
        if (kensakuKey?.referenceDate) {
          query = query.where('yuukouDateArray', 'array-contains', kensakuKey.referenceDate);
        }
        return query;
      }
      ).valueChanges({idField: 'id'}) as unknown as Observable<Layout[]>;
  }

  /**
   * 飼養ロットObservable取得処理(第二期開発)
   * Layout から呼ばれる前提
   * @param kensakuKey
   */
  getShiyoulotObservableForLayout(kensakuKey: SearchKeyForShiyoulot2) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.SHIYOULOT, ref => {
        let query:
          | firestore.CollectionReference
          | firestore.Query = ref;
        if (kensakuKey?.keishaId) {
          query = query.where('keishaId', '==', kensakuKey.keishaId);
          if (kensakuKey?.shikkouDate) {
            query = query.where('dounyuuDate', '<=', kensakuKey.shikkouDate);
          }
          if (kensakuKey?.yuukouDate) {
            query = query.where('dounyuuDate', '>=', kensakuKey.yuukouDate);
          }
        }
        if (kensakuKey?.shiyoulotNumberArray) {
          query = query.where('shiyoulotNumber', 'in', kensakuKey.shiyoulotNumberArray);
        }
        query.orderBy('dounyuuDate');
        query = query.limit(QUERY_LIMIT_SHIYOULOT);
        return query;
      }).valueChanges({idField: 'id'}) as Observable<ShiyoulotV2[]>;
  }

  /**
   * 飼養ロット新規登録
   * @param layout
   */
  registerLayout(layout: Layout) {
    if (!this.sessionService.session.user) { return; }
    const customerRef = this.genCustomerRootCollectionRef(COLLECTION_NAME.LAYOUT);
    const newDoc = customerRef.doc().id;
    // 新規登録
    layout.koushinKaisuu = 1;
    layout.id = newDoc;
    layout.yuukouDateArray = this.getYuukouDateArray(layout.yuukouDate, layout.shikkouDate);
    this.presave(layout);
    customerRef.doc(newDoc).set(layout).then(() => {
      this.messageService.pushInfo('レイアウトを登録しました');
    }).catch((e: any) => {
      console.log(e);
      this.messageService.pushError('レイアウトの登録に失敗しました');
    });
  }

  /**
   * レイアウト更新
   * @param layout
   */
  updateLayout(layout: Layout) {
    if (!this.sessionService.session.user) { return; }
    const customerRef = this.genCustomerRootCollectionRef(COLLECTION_NAME.LAYOUT);
    // 新規登録
    layout.koushinKaisuu++;
    layout.yuukouDateArray = this.getYuukouDateArray(layout.yuukouDate, layout.shikkouDate);
    this.presave(layout);
    customerRef.doc(layout.id).update(layout).then(() => {
      this.messageService.pushInfo('レイアウトを更新しました');
    }).catch(e => {
      console.log(e);
      this.messageService.pushError('レイアウトの更新に失敗しました');
    });
  }

  /**
   * レイアウト削除
   * @param layout
   */
  deleteLayout(layout: Layout) {
    const customerRef = this.genCustomerRootCollectionRef(COLLECTION_NAME.LAYOUT);
    return customerRef.doc(layout.id).delete()
      .then(() => {
        this.messageService.pushInfo('レイアウトの削除に成功しました');
        return Promise.resolve();
      }).catch(e => {
        console.log(e);
        this.messageService.pushError('レイアウトの削除に失敗しました');
        return Promise.reject(e);
      });
  }

  /**
   * 不要なプロパティ削除
   * @param layout
   */
  presave(layout: Layout) {
    layout.colorRefList.forEach(colorRefList => {
      Object.values(colorRefList).forEach(colorRef => {
        this.commonService.deletePropertiesFromObject2(colorRef, ['hover']);
      });
    });
  }

  /**
   * 有効日配列の取得
   * @param yuukouDate
   * @param shikkouDate
   */
  getYuukouDateArray(yuukouDate: string, shikkouDate: string) {
    const diff = this.commonService.getDateDiff(yuukouDate, shikkouDate);
    const yuukouDateArray = [];
    for (let i = 0; i < diff + 1; i++) {
      yuukouDateArray.push(this.commonService.genOffsetDateWithStandardDateString(yuukouDate, i));
    }
    console.log(yuukouDateArray);
    return yuukouDateArray;
  }

  /**
   * レイアウト(編集用) から mat-GridTile (プレビュー用) に変換
   * @param selectedLayout
   */
  setPreview(selectedLayout: Layout) {
    if (!this.selectedKeisha) { return; }
    // 基準のレイアウトセルデータ
    let referenceLayoutCellData: Partial<CellData> = this.genInitCellData();
    let gridTileInfo = this.genGridTileInfo();
    const floors: FloorGridTileInfo = {};
    for (let floorIndex = 0; floorIndex < this.selectedKeisha.layout.floor; floorIndex++) {
      const columns: ColumnGridTileInfo = {};
      for (let columnIndex = 0; columnIndex < this.selectedKeisha.layout.column; columnIndex++) {
        const sides: SideGridTileInfo = {};
        for (let sideIndex = 0; sideIndex < 2; sideIndex++) {
          let gridTileIndex = 0;
          // items: 上中下段ひとまとまりのデータ
          const items: ChunkedGridTileInfo = {};
          for (let rowIndex = 0; rowIndex < this.selectedKeisha.layout.row; rowIndex++) {
            for (let stageIndex = 0; stageIndex < this.selectedKeisha.layout.stage; stageIndex++) {
              // 改行フラグ
              let newLine = false;
              // referenceLayoutCellData が this.genInitCellData() で得られるデータ(初期値)の場合、GridTileにデータをセット。
              // layoutのデータにはx, y は定義されているが、this.genInitCellData()で得られるデータは未定義。
              if (typeof referenceLayoutCellData.x !== "number" || typeof referenceLayoutCellData.y !== "number") {
                referenceLayoutCellData = selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex];
                this.setGridTileInfo(gridTileInfo, floorIndex, columnIndex, sideIndex, rowIndex, stageIndex);
              }
              // 一番最初以外の場合
              if (rowIndex || stageIndex) {
                // 行のどこかで異なるデータがある場合、行の最後までのデータをitemsに入れて、referenceLayoutCellData、gridTileInfoを現在セルデータにする
                if (gridTileInfo.cols !== this.selectedKeisha.layout.stage && !stageIndex) {
                  this.setYposition(gridTileInfo);
                  items[gridTileIndex] = {...gridTileInfo};
                  gridTileIndex++;
                  referenceLayoutCellData = selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex];
                  gridTileInfo = this.genGridTileInfo();
                  this.setGridTileInfo(gridTileInfo, floorIndex, columnIndex, sideIndex, rowIndex, stageIndex);
                  newLine = true;
                }
                if (!newLine) {
                  // 行の途中でreferenceLayoutCellDataと異なるセルがあった時用のフラグ
                  let changePoint = false;
                  // referenceLayoutCellDataと対象セルデータが異なる場合(体測、備考、セル羽数)
                  if (referenceLayoutCellData.taisoku !== selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex].taisoku
                    || selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex].bikou
                    || selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex].cellWasuu) {
                    // 1行前までのデータをitemsに追加し、
                    // 変更がある行は変更前までのセルをitemsに追加
                    // 1番目のセルではない、かつ同じ内容のセルが複数行にまたがっている場合
                    if (stageIndex && gridTileInfo.rows !== 1) {
                      gridTileInfo.cols = this.selectedKeisha.layout.stage;
                      gridTileInfo.rows--;
                      changePoint = true;
                    }
                    this.setYposition(gridTileInfo);
                    items[gridTileIndex] = {...gridTileInfo};
                    gridTileIndex++;
                    // 備考セルまでのデータを保存
                    if (stageIndex && changePoint) {
                      gridTileInfo.cols = stageIndex;
                      gridTileInfo.rows = 1;
                      this.setYposition(gridTileInfo);
                      items[gridTileIndex] = {...gridTileInfo};
                      gridTileIndex++;
                    }
                    // 現在のセルをfirstCell、gridTileInfoに入れる
                    referenceLayoutCellData = selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex];
                    gridTileInfo = this.genGridTileInfo();
                    this.setGridTileInfo(gridTileInfo, floorIndex, columnIndex, sideIndex, rowIndex, stageIndex);
                  } else if (referenceLayoutCellData.shiyoulotNumber === selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex].shiyoulotNumber
                    && referenceLayoutCellData.color === selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex].color
                    && referenceLayoutCellData.taisoku === selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex].taisoku
                    && referenceLayoutCellData.text === selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex].text
                    && referenceLayoutCellData.hidden === selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex].hidden
                    && referenceLayoutCellData.bikou === selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex].bikou
                    && referenceLayoutCellData.cellWasuu === selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex].cellWasuu
                    && referenceLayoutCellData.yPosition === selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex].yPosition
                  ) {
                    // referenceLayoutCellDataと対象セルデータが一致している場合
                    // gridTileInfoのcolをインクリメント。鶏舎のstageを超えた場合、rowをインクリメント。
                    gridTileInfo.cols++;
                    if (gridTileInfo.cols > this.selectedKeisha.layout.stage) {
                      gridTileInfo.rows++;
                      gridTileInfo.cols = 1;
                    }
                  } else {
                    // itemsにgridTileInfoを追加
                    // 1行前までのデータ
                    // 行の途中でデータが異なる場合、col,rowを前の行までにセット
                    // 行の最初の場合はcols=3,row=前の行なので変更不要
                    if (stageIndex && gridTileInfo.rows !== 1) {
                      gridTileInfo.cols = this.selectedKeisha.layout.stage;
                      gridTileInfo.rows--;
                      changePoint = true;
                    }
                    this.setYposition(gridTileInfo);
                    items[gridTileIndex] = {...gridTileInfo};
                    gridTileIndex++;
                    // 行の途中のセルデータが前のセルと異なる場合
                    // 違うセルまでのデータを保存
                    if (stageIndex && changePoint) {
                      gridTileInfo.cols = stageIndex;
                      gridTileInfo.rows = 1;
                      this.setYposition(gridTileInfo);
                      items[gridTileIndex] = {...gridTileInfo};
                      gridTileIndex++;
                    }
                    // 現在のセルをreferenceLayoutCellData、gridTileInfoに入れる
                    referenceLayoutCellData = selectedLayout.layout[floorIndex][columnIndex][sideIndex][rowIndex][stageIndex];
                    gridTileInfo = this.genGridTileInfo();
                    this.setGridTileInfo(gridTileInfo, floorIndex, columnIndex, sideIndex, rowIndex, stageIndex);
                  }
                  // 上中下段最後まで行った場合
                  if (rowIndex === this.selectedKeisha.layout.row - 1
                    && stageIndex === this.selectedKeisha.layout.stage - 1) {
                    this.setYposition(gridTileInfo);
                    items[gridTileIndex] = {...gridTileInfo};
                    referenceLayoutCellData = this.genInitCellData();
                    gridTileInfo = this.genGridTileInfo();
                  }
                }
              }
            }
          }
          sides[sideIndex] = items;
        }
        columns[columnIndex] = sides;
      }
      floors[floorIndex] = columns;
    }
    console.log(floors);
    this.gridList = floors;
  }

  /**
   * Layout のセルデータをグリッドタイルに書き込む
   * @param gridTileInfo
   * @param floor
   * @param column
   * @param side
   * @param row
   * @param stage
   */
  setGridTileInfo(gridTileInfo: GridTileInfo, floor: number, column: number, side: number, row: number, stage: number) {
    const matchedColorRefRow = this.getMatchedColorRef(floor, this.selectedLayout.layout[floor][column][side][row][stage]);
    if (matchedColorRefRow) {
      gridTileInfo.standardWasuu = matchedColorRefRow.standardWasuu;
      gridTileInfo.totalWasuu = matchedColorRefRow.wasuuPerTable[column][side];
    }
    const shiyoulotNumber = this.selectedLayout.layout[floor][column][side][row][stage].shiyoulotNumber;
    if (shiyoulotNumber) {
      const edukeDate = this.getShiyoulotPropertyName(shiyoulotNumber, 'edukeDate');
      const keishuId = this.getShiyoulotPropertyName(shiyoulotNumber, 'keishuId');
      const daihyouShukkaSakiId = this.getShiyoulotPropertyName(shiyoulotNumber, 'daihyouShukkaSakiId');
      gridTileInfo.edukeDate = edukeDate ? edukeDate.slice(-5) : '';
      gridTileInfo.keishuName = this.commonService.getName(this.masterService.keishuMaster, keishuId, 'keishuRyakushou');
      gridTileInfo.customerName = this.commonService.getName(this.masterService.shukkaSakiMaster, daihyouShukkaSakiId, 'shukkaSakiRyakushou');
    }
    gridTileInfo.color = this.selectedLayout.layout[floor][column][side][row][stage].color;
    gridTileInfo.taisoku = this.selectedLayout.layout[floor][column][side][row][stage].taisoku;
    gridTileInfo.bikou = this.selectedLayout.layout[floor][column][side][row][stage].bikou;
    gridTileInfo.cellWasuu = this.selectedLayout.layout[floor][column][side][row][stage].cellWasuu;
    gridTileInfo.text = this.selectedLayout.layout[floor][column][side][row][stage].text;
    gridTileInfo.hidden = this.selectedLayout.layout[floor][column][side][row][stage].hidden;
    gridTileInfo.yPosition = this.selectedLayout.layout[floor][column][side][row][stage].yPosition;
  }

  /**
   * 初期セルデータ
   */
  genInitCellData(): Omit<CellData, 'x' | 'y'> {
    return {
      bikou: '',
      cellWasuu: null,
      color: '',
      hidden: false,
      shiyoulotNumber: null,
      taisoku: false,
      text: '',
      yPosition: 0
    };
  }

  /**
   * 初期グリッド情報
   */
  genGridTileInfo(): GridTileInfo {
    return {
      bikou: '',
      cellWasuu: null,
      color: '',
      cols: 1,
      customerName: '',
      edukeDate: '',
      hidden: false,
      shiyoulotNumber: null,
      keishuName: '',
      rows: 1,
      standardWasuu: 0,
      taisoku: false,
      text: '',
      totalWasuu: 0,
      yPosition: 0
    };
  }

  /**
   * プレビュー画面の gridList から編集した内容(text, hidden, yPosition)を
   * 編集画面の layout に反映
   */
  updateLayoutTableFromGridList() {
    const selectedGridListAxis = this.getSelectedGridListAxis();
    if (!selectedGridListAxis) { return; }
    for (let row = selectedGridListAxis.startPointRow; row <= selectedGridListAxis.endPointRow; row++) {
      for (let stage = selectedGridListAxis.startPointStage; stage <= selectedGridListAxis.endPointStage; stage++) {
        if (this.selectedGridInfo && this.selectedLayout) {
          this.selectedLayout.layout[this.selectedGridInfo.floor][this.selectedGridInfo.column][this.selectedGridInfo.side][row][stage].text = this.selectedGridInfo.gridTile.text;
          this.selectedLayout.layout[this.selectedGridInfo.floor][this.selectedGridInfo.column][this.selectedGridInfo.side][row][stage].hidden = this.selectedGridInfo.gridTile.hidden;
          this.selectedLayout.layout[this.selectedGridInfo.floor][this.selectedGridInfo.column][this.selectedGridInfo.side][row][stage].yPosition = this.selectedGridInfo.gridTile.yPosition;
        }
      }
    }
  }

  /**
   * プレビュー画面の gridList の座標を返す。
   */
  getSelectedGridListAxis() {
    let startPointRow = 0;
    let startPointStage = 0;
    let endPointRow = 0;
    let endPointStage = 0;
    if (this.selectedGridInfo && this.selectedKeisha) {
      const gridTable = this.gridList[this.selectedGridInfo.floor][this.selectedGridInfo.column][this.selectedGridInfo.side];
      for (let index = 0; index <= this.selectedGridInfo.gridTileIndex; index++) {
        // 選択したgridTileの１つ前のgridTileの場合
        if (index !== this.selectedGridInfo.gridTileIndex) {
          if (gridTable[index].rows > 1) {
            if (!index) {
              startPointRow = gridTable[index].rows;
            } else {
              startPointRow += gridTable[index].rows;
            }
          }
          // rows === 1の場合
          else {
            // 1行全て同じ
            if (gridTable[index].cols > 2) {
              startPointRow++;
              startPointStage = 0;
            }
            // セル2つ同じ場合
            else if (gridTable[index].cols > 1) {
              startPointStage += gridTable[index].cols;
              if (startPointStage > 2) {
                startPointRow++;
                startPointStage = 0;
              }
            }
            // セル1つの場合
            else {
              startPointStage++;
              if (startPointStage > 2) {
                startPointRow++;
                startPointStage = 0;
              }
            }
          }
        }
        // 選択したgridTileの場合
        else if (gridTable[index].rows > 1) {
          endPointRow = startPointRow + gridTable[index].rows - 1;
          endPointStage = this.selectedKeisha.layout.stage - 1;
        } else {
          endPointRow = startPointRow;
          endPointStage = startPointStage + gridTable[index].cols - 1;
        }
      }
      return { startPointRow, startPointStage, endPointRow, endPointStage };
    }
    return;
  }

  /**
   * shiyoulotSearvice.ShiyoulotObservable をセット
   */
  setShiyoulotObservable() {
    const kensakuKey = {
      keishaId: this.selectedLayout.keishaId,
      yuukouDate: this.selectedLayout.yuukouDate,
      shikkouDate: this.selectedLayout.shikkouDate,
    };
    this.shiyoulotObservable = this.getShiyoulotObservableForLayout(kensakuKey);
  }

  /**
   * shiyoulotSearvice.ShiyoulotList をセット
   */
  setShiyoulotList() {
    this.setShiyoulotObservable();
    this.shiyoulotSubscription = this.commonService.subScriptionInit(this.shiyoulotSubscription);
    this.shiyoulotSubscription.add(
      this.shiyoulotObservable.subscribe(shiyoulots => {
        this.shiyoulotList = shiyoulots;
      })
    );
  }

  /**
   * shiyoulotSearvice.shiyoulotObservableForDisplay をセット
   */
  getShiyoulotObservableForDisplay() {
    const kensakuKey = { shiyoulotNumberArray: this.selectedLayout.shiyoulotNumberArray };
    this.shiyoulotObservableForDisplay = this.getShiyoulotObservableForLayout(kensakuKey);
  }

  /**
   * shiyoulotSearvice.shiyoulotListForDisplay をセット
   */
  getShiyoulotListForDisplay() {
    this.getShiyoulotObservableForDisplay();
    this.shiyoulotForDisplaySubscription = this.commonService.subScriptionInit(this.shiyoulotForDisplaySubscription);
    this.shiyoulotForDisplaySubscription.add(
      this.shiyoulotObservableForDisplay.subscribe(shiyoulot => {
        this.shiyoulotListForDisplay = shiyoulot;
      })
    );
  }

  /**
   * shiyoulotListForDisplay or shiyoulotList の飼養ロットから特定のプロパティを取得
   * @param shiyoulotNumber
   * @param propaty
   */
  getShiyoulotPropertyName(shiyoulotNumber: number | null, propaty = 'name') {
    let result = '';
    if (shiyoulotNumber === null || typeof shiyoulotNumber === 'undefined') {
      return result;
    }
    if (this.shiyoulotList.length) {
      result = this.commonService.getName(this.shiyoulotList, shiyoulotNumber.toString(), propaty);
    }
    if (this.shiyoulotListForDisplay.length && !result) {
      result = this.commonService.getName(this.shiyoulotListForDisplay, shiyoulotNumber.toString(), propaty);
    }
    return result;
  }

  /**
   * 列羽数の合計を返す
   * @param wasuuPerTable
   */
  calcTotalWasuu(wasuuPerTable: WasuuPerColumn) {
    let totalWasuu = 0;
    for (const column in wasuuPerTable) {
      for (const side in wasuuPerTable[column]) {
        totalWasuu += wasuuPerTable[column][side];
      }
    }
    return totalWasuu;
  }

  /**
   * colorRefList のロットと色の対応情報と Layout のセルデータを比較
   * 飼養ロット番号、色が一致していたら、ロットと色の対応情報を返す
   * TODO result の型を ColorRef に
   * @param floor
   * @param cellData
   */
  getMatchedColorRef(floor: number, cellData: CellData): null | ColorRef {
    let result: null | ColorRef = null;
    this.commonService.keysOf(this.selectedLayout.colorRefList[floor]).forEach((key) => {
      if (this.selectedLayout.colorRefList[floor][key].shiyoulotNumber === cellData.shiyoulotNumber
        && this.selectedLayout.colorRefList[floor][key].color === cellData.color) {
        result = this.selectedLayout.colorRefList[floor][key];
      }
    });
    return result;
  }

  /**
   * 階の羽数の合計を返す
   * @param floorIndex
   */
  getFloorTotalWasuu(floorIndex = -1) {
    let totalWasuu = 0;
    let floor = floorIndex > -1 ? floorIndex : 0;
    const floorLength = floorIndex > -1 ? floorIndex : this.selectedLayout.colorRefList.length;
    for (floor; floor <= floorLength; floor++) {
      for (const colorRefRow in this.selectedLayout.colorRefList[floor]) {
        totalWasuu += this.calcTotalWasuu(this.selectedLayout.colorRefList[floor][colorRefRow].wasuuPerTable);
      }
    }
    return totalWasuu;
  }

  /**
   * gridTile の色を返す
   * @param cellData
   * @param gridTileInfo
   */
  getGridTileColor(gridTileInfo: GridTileInfo) {
    if (!gridTileInfo) { return; }
    let color = COLOR_SHUBETSU.VACANT; // 「空」の色
    if (gridTileInfo.color && !gridTileInfo.taisoku) {
      color = gridTileInfo.color;
    } else if (gridTileInfo.taisoku) {
      color = COLOR_SHUBETSU.TAISOKU;
    }
    return color;
  }

  /**
   * 備考
   * CellData or gridTileInfo
   * @param cellData
   * @param cellData.bikou
   * @param cellData.cellWasuu
   */
  dispBikou(cellData: { bikou: string, cellWasuu: number | null }) {
    if (!cellData) { return ''; }
    return cellData.bikou && cellData.cellWasuu !== null && typeof cellData.cellWasuu !== 'undefined'
      ? cellData.bikou + cellData.cellWasuu.toString() : cellData.bikou
        ? cellData.bikou : cellData.cellWasuu !== null && typeof cellData.cellWasuu !== 'undefined'
          ? cellData.cellWasuu.toString() : '';
  }


  /**
   * sameShiyoulotWasuuPerFloorList に階毎の飼養ロットの羽数の合計をセット
   * @param colorRefList
   */
  getSameShiyoulotWasuuPerFloor(colorRefList: ColorRefList[]) {
    this.sameShiyoulotWasuuPerFloorList = [];
    let tempColorRefItem: ColorRef;
    for (let floor = 0; floor < colorRefList.length - 1; floor++) {
      for (const thisFloorItemIndex in colorRefList[floor]) {
        tempColorRefItem = colorRefList[floor][thisFloorItemIndex];// 基準 ColorRef データ
        const sameTempColorRefItemExist = this.sameShiyoulotWasuuPerFloorList.find(shiyoulotInfo => {
          return shiyoulotInfo.shiyoulotNumber === tempColorRefItem.shiyoulotNumber && shiyoulotInfo.color === tempColorRefItem.color;
        });
        if (!sameTempColorRefItemExist) {
          this.sameShiyoulotWasuuPerFloorList.push({
            shiyoulotNumber: tempColorRefItem.shiyoulotNumber,
            color: tempColorRefItem.color,
            wasuuArray: [{floor: floor, totalWasuu: this.calcTotalWasuu(tempColorRefItem.wasuuPerTable)}]
          });
          for (let nextFloor = floor+1; nextFloor < colorRefList.length; nextFloor++) {
            const sameShiyoulotItem = Object.values(colorRefList[nextFloor]).find(colorRefItem => {
              return tempColorRefItem.color === colorRefItem.color && tempColorRefItem.shiyoulotNumber === colorRefItem.shiyoulotNumber;
            });
            if (sameShiyoulotItem) {
              const sameShiyoulotWasuuPerFloor = this.sameShiyoulotWasuuPerFloorList.find(shiyoulotInfo => {
                return shiyoulotInfo.shiyoulotNumber === sameShiyoulotItem.shiyoulotNumber && shiyoulotInfo.color === sameShiyoulotItem.color;
              });
              if (sameShiyoulotWasuuPerFloor) {
                sameShiyoulotWasuuPerFloor.wasuuArray.push({
                  floor: nextFloor,
                  totalWasuu: this.calcTotalWasuu(sameShiyoulotItem.wasuuPerTable)
                });
              }
            }
          }
        }
      }
    }
    this.sameShiyoulotWasuuPerFloorList = this.sameShiyoulotWasuuPerFloorList.filter(value => {
      return value.wasuuArray.length > 1 && value.shiyoulotNumber;
    });
    for (const floor of this.sameShiyoulotWasuuPerFloorList) {
      floor.wasuuArray.reverse();
    }
  }

  /**
   * 飼養ロットの各階の羽数配列の合計を返す
   * @param wasuuTable
   */
  getSumWasuu(wasuuTable: {floor: number, totalWasuu: number}[]) {
    let result = 0;
    if (wasuuTable.length) {
      for (const floorWasuu of wasuuTable) {
        result += floorWasuu.totalWasuu;
      }
    }
    return result;
  }

  /**
   *
   * @param text
   */
  getLines(text: string) {
    if (!text) { return 0; }
    const pattern = /\r\n|\n/g;
    const num = text.match(pattern);
    const lines = !num ? 1 : 1 + num.length;
    return lines;
  }

  /**
   * gridTile の y 在標をセット
   * @param gridTileInfo
   */
  setYposition(gridTileInfo: GridTileInfo) {
    const ypositionCoefficient = 8;
    let gridLines = 0;
    gridLines += this.getLines(gridTileInfo.text);
    gridLines += gridTileInfo.bikou || gridTileInfo.cellWasuu ? 1 : 0;
    if (!gridTileInfo.hidden && gridTileInfo.color) {
      if (gridTileInfo.rows === 1) {
        gridLines++;
      } else if (gridTileInfo.rows === 2 || gridTileInfo.rows === 3) {
        gridLines += 2;
      } else if (gridTileInfo.rows === 4) {
        gridLines += 4;
      } else if (gridTileInfo.rows === 5) {
        gridLines += 5;
      } else if (gridTileInfo.rows > 5) {
        gridLines += 6;
      }
    }
    const threshold = (gridTileInfo.rows - gridLines) * ypositionCoefficient + gridLines * 2 - 1;
    if (Math.abs(gridTileInfo.yPosition) > threshold) {
      gridTileInfo.yPosition = 0;
    }
  }

  /**
   * 対象の飼養ロット番号が飼養ロットリストに存在しない場合trueを返す
   * @param selectedShiyoulotNumber
   */
  notIncludeShiyoulotList(selectedShiyoulotNumber: number | null) {
    return !selectedShiyoulotNumber || !this.shiyoulotList.map(shiyoulot => shiyoulot.shiyoulotNumber).includes(selectedShiyoulotNumber);
  }
}
